import { api } from '../lib/api-client.js';

import type { ActivityEntry, UnitRow } from './buildings.js';
import type {
  CreateUnitCustomFieldDefinitionInput,
  ReorderUnitCustomFieldsInput,
  UnitCustomFieldOption,
  UnitCustomFieldType,
  UnitDocumentKind,
  UnitDocumentPresignInput,
  UpdateUnitCustomFieldDefinitionInput,
  UpdateUnitDocumentInput,
} from '@sitewise/shared';


/* ----------------------------- Detail ------------------------------- */

export interface UnitDetailBuilding {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  borough: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface UnitFieldValue {
  definitionId: string;
  key: string;
  label: string;
  fieldType: UnitCustomFieldType;
  options: UnitCustomFieldOption[] | null;
  /** Shape depends on `fieldType`. `null` means the unit has no
   *  value set for this field yet. */
  value: unknown;
  updatedAt: string;
}

export interface UnitDetail {
  unit: UnitRow;
  building: UnitDetailBuilding;
  customFieldValues: UnitFieldValue[];
  deviceCounts: { complianceTracked: number; inventory: number };
  openWorkOrderCount: number;
  recentInspectionCount: number;
  documentCount: number;
}

export async function fetchUnit(unitId: string): Promise<UnitDetail> {
  return api(`/units/${unitId}`);
}

export async function fetchUnitActivity(unitId: string, limit = 50) {
  return api<{ entries: ActivityEntry[] }>(`/units/${unitId}/activity?limit=${limit}`);
}

/* ----------------------------- Custom-field values ------------------ */

export async function setUnitCustomFieldValue(
  unitId: string,
  fieldKey: string,
  value: unknown,
) {
  return api<{
    value: { definitionId: string; value: unknown; updatedAt: string };
  }>(`/units/${unitId}/custom-fields/${encodeURIComponent(fieldKey)}`, {
    method: 'PUT',
    body: { value },
  });
}

export async function clearUnitCustomFieldValue(unitId: string, fieldKey: string): Promise<void> {
  await api(`/units/${unitId}/custom-fields/${encodeURIComponent(fieldKey)}`, {
    method: 'DELETE',
  });
}

/* ----------------------------- Field definitions -------------------- */

export interface UnitFieldDefinition {
  id: string;
  orgId: string;
  key: string;
  label: string;
  fieldType: UnitCustomFieldType;
  options: UnitCustomFieldOption[] | null;
  helpText: string | null;
  displayOrder: number;
  isArchived: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listUnitFieldDefinitions(opts: { includeArchived?: boolean } = {}) {
  const qs = opts.includeArchived ? '?includeArchived=true' : '';
  return api<{ definitions: UnitFieldDefinition[] }>(`/unit-fields${qs}`);
}

export async function createUnitFieldDefinition(input: CreateUnitCustomFieldDefinitionInput) {
  return api<{ definition: UnitFieldDefinition }>('/unit-fields', {
    method: 'POST',
    body: input,
  });
}

export async function updateUnitFieldDefinition(
  id: string,
  input: UpdateUnitCustomFieldDefinitionInput,
) {
  return api<{ definition: UnitFieldDefinition }>(`/unit-fields/${id}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function archiveUnitFieldDefinition(id: string): Promise<void> {
  await api(`/unit-fields/${id}/archive`, { method: 'POST' });
}

export async function restoreUnitFieldDefinition(id: string) {
  return api<{ definition: UnitFieldDefinition }>(`/unit-fields/${id}/restore`, {
    method: 'POST',
  });
}

export async function reorderUnitFieldDefinitions(input: ReorderUnitCustomFieldsInput) {
  return api<{ definitions: UnitFieldDefinition[] }>('/unit-fields/reorder', {
    method: 'POST',
    body: input,
  });
}

/* ----------------------------- Documents ---------------------------- */

export interface UnitDocument {
  id: string;
  unitId: string;
  kind: UnitDocumentKind;
  storageKey: string;
  originalFilename: string;
  contentType: string;
  bytes: number;
  caption: string | null;
  uploadedByUserId: string | null;
  createdAt: string;
}

export async function listUnitDocuments(unitId: string) {
  return api<{ documents: UnitDocument[]; total: number }>(`/units/${unitId}/documents`);
}

export interface PresignUnitDocumentResponse {
  document: UnitDocument;
  uploadId: string;
  uploadUrl: string;
  objectKey: string;
  headers: Record<string, string>;
  expiresAt: string;
}

export async function presignUnitDocument(unitId: string, input: UnitDocumentPresignInput) {
  return api<PresignUnitDocumentResponse>(`/units/${unitId}/documents/presign`, {
    method: 'POST',
    body: input,
  });
}

export async function confirmUnitDocument(
  unitId: string,
  documentId: string,
  caption?: string | null,
) {
  return api<{ document: UnitDocument }>(`/units/${unitId}/documents/${documentId}/confirm`, {
    method: 'POST',
    body: caption !== undefined ? { caption } : {},
  });
}

export async function fetchUnitDocumentSignedUrl(unitId: string, documentId: string) {
  return api<{ url: string; expiresAt: string }>(
    `/units/${unitId}/documents/${documentId}/signed-url`,
  );
}

export async function updateUnitDocument(
  unitId: string,
  documentId: string,
  input: UpdateUnitDocumentInput,
) {
  return api<{ document: UnitDocument }>(`/units/${unitId}/documents/${documentId}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function deleteUnitDocument(unitId: string, documentId: string): Promise<void> {
  await api(`/units/${unitId}/documents/${documentId}`, { method: 'DELETE' });
}
